import React from 'react';
import { FileCheck, AlertCircle, CheckCircle2, XCircle } from 'lucide-react';
import { cn } from '@/lib/utils';

const ScoreCard = ({ score, status, documentCategory, riskLevel }) => {
  const getScoreColor = (score) => {
    if (score >= 90) return 'text-verify';
    if (score >= 75) return 'text-verify';
    if (score >= 60) return 'text-warning';
    return 'text-destructive';
  };

  const getStatusIcon = () => {
    switch(status) {
      case 'ready': return <CheckCircle2 className="w-6 h-6 text-verify" />;
      case 'warning': return <AlertCircle className="w-6 h-6 text-warning" />;
      case 'reject-risk': return <XCircle className="w-6 h-6 text-destructive" />;
      default: return <FileCheck className="w-6 h-6 text-muted-foreground" />;
    }
  };

  const getStatusLabel = () => {
    switch(status) {
      case 'ready': return 'Ready to Submit';
      case 'warning': return 'Needs Attention';
      case 'reject-risk': return 'Rejection Risk';
      default: return 'Unknown';
    }
  };

  const getRiskLevelColor = (level) => {
    switch(level) {
      case 'low': return 'bg-verify/10 text-verify border-verify/20';
      case 'medium': return 'bg-warning/10 text-warning border-warning/20';
      case 'high': return 'bg-destructive/10 text-destructive border-destructive/20';
      default: return 'bg-muted text-muted-foreground border-border';
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm" data-testid="score-card">
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="flex items-center gap-2 mb-2">
            {getStatusIcon()}
            <span className="font-public font-semibold text-slate-900">{getStatusLabel()}</span>
          </div>
          <p className="text-sm text-slate-600">Document: {documentCategory}</p>
        </div>
        <div className={cn(
          "px-3 py-1 rounded-md border font-mono text-xs uppercase tracking-wider",
          getRiskLevelColor(riskLevel)
        )} data-testid="risk-level-badge">
          {riskLevel} risk
        </div>
      </div>

      <div className="text-center py-8 border-y border-slate-200">
        <div className="mb-2">
          <span className={cn(
            "font-outfit text-6xl font-bold",
            getScoreColor(score)
          )} data-testid="readiness-score">
            {score}
          </span>
          <span className="text-2xl text-slate-400">/100</span>
        </div>
        <p className="font-public text-sm text-slate-600">Readiness Score</p>
      </div>

      <div className="mt-6">
        <div className="w-full bg-slate-200 rounded-full h-2 overflow-hidden">
          <div
            className={cn(
              "h-full rounded-full transition-all duration-500",
              score >= 75 ? 'bg-verify' : score >= 60 ? 'bg-warning' : 'bg-destructive'
            )}
            style={{ width: `${score}%` }}
          />
        </div>
      </div>
    </div>
  );
};

export default ScoreCard;
